import {useEffect, useState} from "react";
import { calculateTotals } from "@/utils/cart";
import CartList from "./components/CartList";
import CartTotalPricing from "./components/CartTotalPricing";
import {fetchCartById} from "@/api/fetchCartById.ts";
import {useGetProductsQuery} from "@/redux-store/api/marketingApi.ts";
import {useAppDispatch, useAppSelector} from "@/redux-store/hooks.ts";
import {setCartId} from "@/redux-store/cart/cartSlice.ts";
import { ROUTES } from "@/utils/navigation";
import { useNavigate } from "react-router-dom";

interface IDiscountApplied {
  id: string;
  name: string;
  description: string;
  code: string;
  percentageOff: number;
  fixedAmount: number | null;
  lastEdited: string;
}

interface ICartItem {
  idProduct: number;
  productName: string;
  quantity: number;
  price: number;
  stockAvailable: number;
}

export interface IPaymentResponse {
  message: string;
  orderNumber: string;
  totalAmount: number;
  subTotalAmount: number;
  discountApplied: IDiscountApplied;
  cartItems: ICartItem[];
  shopShippingMethod: {
    price: number
  }
  tax: number
}

interface IProps {
  orderSummary: IOrderSummary;
  setOrderSummary: React.Dispatch<React.SetStateAction<IOrderSummary>>;
}

const CartSummary: React.FC<IProps> = ({ orderSummary, setOrderSummary }) => {
  const { data } = useGetProductsQuery({});
  const [products, setProducts] = useState<ICartProduct[]>([]);
  const sessionId = useAppSelector((state) => state.cart.sessionId);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  useEffect(() => {
    const getCheckoutData = async (id: string) => {
      try {
        const checkoutData = await fetchCartById(id);
        dispatch(setCartId(checkoutData?.id || ""));
        if (data) {
          const updatedProducts = checkoutData?.items?.map((item) => {
            const productData = data.find((product) => product.id === item.idProduct);
            return {
              ...item,
              name: productData?.name || "No Name",
              image: productData?.image || "No Image",
            };
          }) || [];
          setProducts(updatedProducts);
        } else {
          setProducts(checkoutData?.items || []);
        }
      } catch (error: any) {
        console.error("Error fetching checkout:", error);
        if (error.response?.status === 404) {
          navigate(ROUTES.home);
        }
      }
    };

    if (data) {
      getCheckoutData(sessionId);
    }
  }, [data, sessionId]);

  // Get full product data and update order summary products and subtotal
  useEffect(() => {
    const order: IOrderProduct[] = products.map(({ id, quantity, price }) => ({
      id,
      quantity,
      price,
    }));
    const { totalAmount: subtotal } = calculateTotals(products);
    setOrderSummary((prevState) => ({ ...prevState, order, subtotal }));
  }, [products, setOrderSummary]);

  return (
    <>
      {products && products.length > 0 && (
        <>
          <CartList products={products} />
          <CartTotalPricing orderSummary={orderSummary} />
        </>
      )}
    </>
  );
};

export default CartSummary;
